"use client";

import React from "react";
import { CalendarCheck, Compass, FileCheck2, Hammer, KeyRound, ArrowRight, ShieldAlert, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ProcessStep {
  number: string;
  title: string;
  phase: string;
  duration: string;
  description: string;
  deliverables: string[];
  icon: React.ComponentType<{
    className?: string;
  }>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const steps: ProcessStep[] = [{
  number: "01",
  phase: "Discovery & Feasibility",
  title: "Initial consultation & site assessment",
  duration: "Week 1",
  description: "We visit your property to evaluate lot lines, zoning setbacks, structural integration, and your household space goals. We discuss scope, initial budget expectations, and feasible footprint expansions.",
  deliverables: ["Property boundary & setback review", "Structural tie-in assessment", "Transparent initial budget framework"],
  icon: CalendarCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_consultation_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_consultation_walkthrough.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_consultation_walkthrough.png-wvc-sizes",
    alt: "Architectural contractor discussing home addition layout with homeowners at kitchen island"
  }
}, {
  number: "02",
  phase: "Architecture & Selection",
  title: "Architectural design & planning",
  duration: "Weeks 2–5",
  description: "Our architects draft complete schematics, 3D renderings, and structural engineering packages. You select exterior materials, rooflines, millwork, and finishes so every detail is locked before permitting.",
  deliverables: ["Comprehensive 3D schematic models", "Full interior & exterior material selections", "Engineered structural blueprint set"],
  icon: Compass,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_architectural_blueprints.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_architectural_blueprints.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_architectural_blueprints.webp-wvc-sizes",
    alt: "Detailed architectural blueprints and material samples for an expanded primary suite addition"
  }
}, {
  number: "03",
  phase: "Compliance & Prep",
  title: "Permitting & municipal approval",
  duration: "Weeks 6–8",
  description: "We prepare and submit all municipal paperwork, manage zoning reviews, coordinate plan checks, and secure required building, electrical, and plumbing permits before breaking ground.",
  deliverables: ["Municipal permit submission & tracking", "HOA and neighborhood board documentation", "Finalized fixed-price construction schedule"],
  icon: FileCheck2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_permitting_documents.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_permitting_documents.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_permitting_documents.webp-wvc-sizes",
    alt: "Official municipal building permit stamps and site plans on project table"
  }
}, {
  number: "04",
  phase: "Active Build",
  title: "Precision framing & construction",
  duration: "Weeks 9–20",
  description: "Foundation excavation, framing, roof tie-in, MEP rough-ins, insulation, drywall, and custom finishes. An on-site project coordinator provides weekly milestone walkthroughs and daily progress logs.",
  deliverables: ["Dust and moisture protection barriers", "Daily project logs via builder portal", "Passed trade inspections at each milestone"],
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png-wvc-sizes",
    alt: "Clean framing and structural lumber for a two-story home addition under construction"
  }
}, {
  number: "05",
  phase: "Turnover & Warranty",
  title: "Final walkthrough & key handover",
  duration: "Final Week",
  description: "Together we complete a rigorous punch-list inspection. We deliver mechanical warranties, certificate of occupancy, maintenance manuals, and officially hand over your completed living space.",
  deliverables: ["Certificate of occupancy from local municipality", "Complete warranty & maintenance binder", "12-month post-completion check-in schedule"],
  icon: KeyRound,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_completed_sunroom_suite.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_completed_sunroom_suite.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_completed_sunroom_suite.png-wvc-sizes",
    alt: "Completed light-filled luxury home addition with expansive windows and hardwood floors"
  }
}];
export default function HomeAdditionProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="homeadditionprocess" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 md:mb-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="border-primary/40 bg-accent/40 text-primary-foreground font-mono uppercase tracking-wider text-xs px-2.5 py-0.5">
              5-Step Build Path
            </Badge>
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
              From Blueprint to Move-In
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-normal tracking-tight text-foreground text-balance">
            A clear, predictable approach to expanding your home
          </h2>

          <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl font-sans">
            Adding square footage should not feel chaotic. Our systematic five-stage
            framework ensures your addition integrates with your existing architecture,
            abides by local building codes, and finishes on schedule.
          </p>
        </div>

        {/* 5-Step Vertical Flow */}
        <div className="relative space-y-12 md:space-y-16">
          {/* Connector Line behind steps for desktop */}
          <div className="hidden lg:block absolute left-8 top-10 bottom-10 w-px bg-border/80" aria-hidden="true" />

          {steps.map((step, index) => {
          const Icon = step.icon;
          const isReversed = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`relative transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${index * 120}ms`
          }}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
                  {/* Step Badge / Milestone Indicator */}
                  <div className="lg:col-span-1 flex lg:flex-col items-center justify-start gap-3">
                    <div className="relative z-10 flex size-14 shrink-0 items-center justify-center rounded-xl bg-secondary text-secondary-foreground shadow-md ring-4 ring-background border border-border">
                      <span className="font-mono text-base font-semibold text-primary">
                        {step.number}
                      </span>
                    </div>
                    <div className="h-px flex-1 bg-border lg:hidden" />
                  </div>

                  {/* Main Content Card */}
                  <div className={`lg:col-span-6 ${isReversed ? "lg:order-3" : "lg:order-2"}`}>
                    <Card className="border border-border/80 shadow-sm bg-card hover:shadow-md transition-shadow duration-300">
                      <CardContent className="p-6 sm:p-8">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <span className="text-xs font-mono font-medium uppercase tracking-wider text-primary">
                            {step.phase}
                          </span>
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted text-foreground/70">
                            {step.duration}
                          </span>
                        </div>

                        <div className="flex items-start gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-accent text-secondary shrink-0 mt-0.5">
                            <Icon className="size-5 text-secondary" />
                          </div>
                          <h3 className="text-xl sm:text-2xl font-serif font-medium text-card-foreground tracking-tight">
                            {step.title}
                          </h3>
                        </div>

                        <p className="text-sm sm:text-base text-foreground/80 leading-relaxed mb-6 font-sans">
                          {step.description}
                        </p>

                        <Separator className="my-4 bg-border/60" />

                        <div className="space-y-2">
                          <p className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
                            Key Deliverables &amp; Milestones:
                          </p>
                          <ul className="space-y-1.5">
                            {step.deliverables.map((item, itemIdx) => <li key={itemIdx} className="flex items-center gap-2 text-xs sm:text-sm text-foreground/85">
                                <span className="size-1.5 rounded-full bg-primary shrink-0" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Step Image */}
                  <div className={`lg:col-span-5 ${isReversed ? "lg:order-2" : "lg:order-3"}`}>
                    <div className="group relative overflow-hidden rounded-xl border border-border shadow-sm bg-muted aspect-4/3">
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-103" />
                      <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent opacity-60" />
                      <div className="absolute bottom-3 left-3 right-3 text-xs font-mono text-white/90 drop-shadow-xs bg-black/40 backdrop-blur-xs px-2.5 py-1.5 rounded border border-white/10 flex items-center justify-between">
                        <span>Phase {step.number} Verification</span>
                        <span className="text-primary font-sans font-medium">Standard</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Section Footer / Consultation Anchor */}
        <div className={`mt-16 md:mt-24 p-8 sm:p-10 rounded-2xl bg-secondary text-secondary-foreground border border-border/20 shadow-lg transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionDelay: "450ms"
      }}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary">
                <Sparkles className="size-4" />
                <span>Ready to expand your footprint?</span>
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-normal tracking-tight text-secondary-foreground">
                Discuss your home addition with our project team
              </h3>
              <p className="text-sm sm:text-base text-secondary-foreground/80 max-w-2xl font-sans">
                Book an on-site feasibility consultation to review setbacks, zoning limits,
                and architectural opportunities for your property.
              </p>
            </div>

            <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col items-start md:items-end justify-center gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-2.5 shadow-sm text-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border/40 text-secondary-foreground hover:bg-secondary/60 text-sm">
                <Link to="/OurProjectsPage">Explore completed projects</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}